import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const CATEGORIES = ["All", "Pokemon", "Sealed", "Basketball", "Football", "Baseball", "Hockey", "Sports"];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "All";
  const searchParam = params.get("search") || "";
  const [term, setTerm] = useState(searchParam);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setTerm(searchParam); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (active !== "All") p.category = active;
    if (searchParam) p.search = searchParam;
    api.get("/products", { params: p })
      .then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [active, searchParam]);

  const updateParams = (next) => {
    const obj = {};
    if (active !== "All") obj.category = active;
    if (next.category !== undefined) { if (next.category !== "All") obj.category = next.category; else delete obj.category; }
    const s = next.search !== undefined ? next.search : searchParam;
    if (s) obj.search = s;
    setParams(obj);
  };

  const submitSearch = (e) => { e.preventDefault(); updateParams({ search: term.trim() }); };
  const clearSearch = () => { setTerm(""); updateParams({ search: "" }); };

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
      <p className="overline text-secondary">The Vault</p>
      <h1 className="mt-2 font-display text-4xl font-900 tracking-tighter text-white sm:text-5xl">Shop Cards</h1>

      <form onSubmit={submitSearch} className="mt-8 flex max-w-xl items-center gap-2 rounded-full border border-white/10 bg-card px-4 py-1 focus-within:border-primary">
        <Search size={18} className="text-zinc-500" />
        <input
          data-testid="search-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search by card or set name…"
          className="w-full bg-transparent py-2.5 text-white outline-none placeholder:text-zinc-600"
        />
        {term && (
          <button type="button" onClick={clearSearch} data-testid="search-clear" className="text-zinc-500 hover:text-white"><X size={16} /></button>
        )}
        <button type="submit" data-testid="search-submit" className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5">Search</button>
      </form>

      <div className="mt-6 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button key={c} data-testid={`filter-${c.toLowerCase()}`}
            onClick={() => updateParams({ category: c })}
            className={`rounded-full border px-5 py-2 text-sm transition-colors ${
              active === c ? "border-primary bg-primary text-white" : "border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white"
            }`}>
            {c}
          </button>
        ))}
      </div>

      {searchParam && (
        <p className="mt-6 text-sm text-zinc-400" data-testid="search-results-label">
          {products.length} result{products.length === 1 ? "" : "s"} for “{searchParam}”
        </p>
      )}

      {loading ? (
        <div className="grid grid-cols-2 gap-4 pt-10 sm:gap-6 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl border border-white/10 bg-zinc-900" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="py-24 text-center text-zinc-500" data-testid="empty-catalog">No cards match your search.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 pt-10 sm:gap-6 lg:grid-cols-4" data-testid="catalog-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
